import React, { useContext, useState, useRef } from "react";
import { toast } from "react-toastify";
import { UserApiContext } from "../../../../UserDetailsContext/UserDetailsContext";
import {
  downloadStandardFormat,
  readExcelFile,
  trackUserUploadsMastersFileForBulkUploads,
} from "../../Utility/utility";

const MM_HEADERS = [
  "Sr No.",
  "Material Description*",
  "Material Type*",
  "Material Category*",
  "Material Code*",
  "Length*",
  "Breadth*",
  "Height*",
  "Volume*",
  "Weight*",
];

/**
 * Step 1 for upload file in material master
 */
const UploadFile = ({
  setStep = "",
  setUploadedData,
  companyID,
}) => {
  const [errors, setErrors] = useState([]);
  const { userDetails } = useContext(UserApiContext);
  const fileInputRef = useRef(null);
  const [errorTypes, setErrorTypes] = useState({
    wrongFileType: false,
    headersMismatch: false,
    missingMandatoryFields: false,
    invalidNumericValues: false,
  });

  /**
   * Function to validate excel data
   * @param {array} data
   * @returns {number}
   */
  const validateData = (data) => {
    const errors = [];
    let newErrorTypes = {};

    if (data.length === 0) {
      toast.info("Please Upload a valid File.");
      return false;
    }

    const headers = Object.keys(data[0] || {});

    // Check if all required columns are present
    const missingColumns = MM_HEADERS.filter((col) => !headers.includes(col));

    if (missingColumns.length > 0) {
      errors.push(`Missing required columns: ${missingColumns.join(", ")}`);
      newErrorTypes.headersMismatch = true;
    }

    // Validate data rows
    data.forEach((row, i) => {
      // Auto-calculate volume from LBH if it is missing or empty, but LBH are valid
      if (
        (row["Volume*"] === undefined || row["Volume*"] === "" || row["Volume*"] === null) &&
        row["Length*"] !== undefined && row["Length*"] !== "" && !isNaN(parseFloat(row["Length*"])) &&
        row["Breadth*"] !== undefined && row["Breadth*"] !== "" && !isNaN(parseFloat(row["Breadth*"])) &&
        row["Height*"] !== undefined && row["Height*"] !== "" && !isNaN(parseFloat(row["Height*"]))
      ) {
        row["Volume*"] = parseFloat(row["Length*"]) * parseFloat(row["Breadth*"]) * parseFloat(row["Height*"]);
      }

      MM_HEADERS.forEach((col) => {
        const value = row[col];
        if (value === undefined || value === "") {
          errors.push(`Row ${i + 2}: ${col} cannot be empty`);
          newErrorTypes.missingMandatoryFields = true;
        }
      });

      // validations for numeric columns
      ["Length*", "Breadth*", "Height*", "Volume*", "Weight*"].forEach((col) => {
        const value = row[col];
        if (value !== undefined && value !== "" && isNaN(parseFloat(value))) {
          errors.push(`Row ${i + 2}: ${col} must be a number`);
          newErrorTypes.invalidNumericValues = true;
        }
      });
    });

    setErrors(errors);
    setErrorTypes(newErrorTypes);
    return errors.length === 0;
  };

   /**
   * Handles the file upload, including validation.
   * @param {Event} e - The file input change event.
   */
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      resetFileAndErrors();

      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setErrorTypes((prev) => ({ ...prev, wrongFileType: true }));
        setErrors(["The file must be in .xlsx or .xls format."]);
        return;
      }

      const { data, error } = await readExcelFile(selectedFile);

      if (error) {
        toast.error(error);
        return;
      }

      // Filter out rows that are effectively empty (only have formula populated cells, but no actual data entered by user)
      const activeRows = (data || []).filter((row) => {
        return [
          "Material Description*",
          "Material Type*",
          "Material Category*",
          "Material Code*",
          "Length*",
          "Breadth*",
          "Height*",
          "Weight*"
        ].some(col => row[col] !== undefined && row[col] !== null && row[col].toString().trim() !== "");
      });

      if (validateData(activeRows)) {
        setUploadedData(activeRows);
        setStep(1);
      }

      trackUserUploadsMastersFileForBulkUploads(
        "Material",
        userDetails,
        companyID,
        selectedFile.name
      );
    }
  };

  
  /**
  * Reset file input, errors and error types 
  * @returns {void}
  */
  const resetFileAndErrors = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setErrors([]);
    setErrorTypes({
      wrongFileType: false,
      headersMismatch: false,
      missingMandatoryFields: false,
      invalidNumericValues: false,
    });
  };

  return (
    <div className="card-body overflow-auto" style={{ maxHeight: "460px" }}>
      <div className="mb-4 text-center">
        <button
          className="btn btn-primary"
          onClick={() => {
            downloadStandardFormat(MM_HEADERS, "MaterialMaster");
          }}
        >
          Download Standard Format
        </button>
      </div>
      <div className="alert alert-warning" style={{ margin: 0 }}>
        <h5 className="alert-heading under">
          <u>File Requirements</u>:
        </h5>
        <ul>
          <li
            style={{
              color: errorTypes.wrongFileType ? "red" : "inherit",
              fontWeight: errorTypes.wrongFileType ? "bold" : "normal",
            }}
          >
            The file must be in .xlsx or .xls format.
            {errorTypes.wrongFileType && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.headersMismatch ? "red" : "inherit",
              fontWeight: errorTypes.headersMismatch ? "bold" : "normal",
            }}
          >
            Ensure that headers match the standard format.
            {errorTypes.headersMismatch && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.missingMandatoryFields ? "red" : "inherit",
              fontWeight: errorTypes.missingMandatoryFields ? "bold" : "normal",
            }}
          >
            All fields are <u>mandatory</u>.
            {errorTypes.missingMandatoryFields && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.invalidNumericValues ? "red" : "inherit",
              fontWeight: errorTypes.invalidNumericValues ? "bold" : "normal",
            }}
          >
            Length, Breadth, Height, Volume, and Weight must be valid numbers.
            {errorTypes.invalidNumericValues && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.invalidNumericValues ? "red" : "inherit",
              fontWeight: errorTypes.invalidNumericValues ? "bold" : "normal",
            }}
          >
            <u>Material Code</u> should be unique.
          </li>
        </ul>
      </div>
      <div className="text-center">
        <label className="btn btn-success upload-file-button-enquiry-my-account m-4">
          <input
            type="file"
            className="form-control d-none"
            id="upload_file_button"
            onChange={handleFileChange}
            accept=".xlsx,.xls"
            ref={fileInputRef}
          />
          <i className="fas fa-upload mr-2"></i>Upload File
        </label>
      </div>
      {errors.length > 0 && (
        <div>
          <h6 className="text-danger">Errors:</h6>
          <ul className="list-group">
            {errors.map((error, index) => (
              <li
                key={index}
                className="list-group-item list-group-item-danger"
              >
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UploadFile;
